import { useState, useCallback } from 'react';
import { useMountedState } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';

import { fetchLegalBasisSearch } from 'APIPackage/CatalogsModule/services/legalBasisApi';
import { ILegalBasisSearchParams } from 'APIPackage/CatalogsModule/interfaces/ILegalBasisSearchParams';
import { ILegalBasisDocumentExtended } from 'APIPackage/CatalogsModule/interfaces/ILegalBasisDocumentExtended';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

export function useLegalBasisSearch() {
  const isMounted = useMountedState();
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [legalBasisDocuments, setLegalBasisDocuments] = useState<ILegalBasisDocumentExtended[]>();
  const [currentSearchParams, setCurrentSearchParams] = useState<ILegalBasisSearchParams>();
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const searchLegalBasisDocuments = useCallback(
    async (params: ILegalBasisSearchParams) => {
      try {
        dispatch(coreSetLoading(true));
        const response = await fetchLegalBasisSearch(params);

        if (isMounted()) {
          setLegalBasisDocuments(
            response.list.map(document => ({
              ...document,
              id: uuidv4(),
            })),
          );
          setCurrentSearchParams(params);
          setPage(response.page);
          setPageCount(response.pageCount);
        }
      } finally {
        dispatch(coreSetLoading(false));
      }
    },
    [dispatch, isMounted],
  );

  const getSearchLegalBasisDocumentsNextPage = useCallback(async () => {
    if (currentSearchParams) {
      try {
        dispatch(coreSetLoading(true));
        const params = {
          ...currentSearchParams,
          pagination: {
            ...currentSearchParams.pagination,
            page: (page + 1).toString(),
          },
        };
        const response = await fetchLegalBasisSearch(params);

        if (isMounted()) {
          setLegalBasisDocuments(prev => [
            ...(prev || []),
            ...response.list.map(document => ({
              ...document,
              id: uuidv4(),
            })),
          ]);
          setCurrentSearchParams(params);
          setPage(response.page);
          setPageCount(response.pageCount);
        }
      } finally {
        dispatch(coreSetLoading(false));
      }
    }
  }, [currentSearchParams, dispatch, page, isMounted]);

  const resetSearchLegalBasisDocuments = useCallback(() => {
    setLegalBasisDocuments(undefined);
    setCurrentSearchParams(undefined);
    setPage(0);
    setPageCount(0);
  }, []);

  return {
    searchLegalBasisDocuments,
    getSearchLegalBasisDocumentsNextPage,
    resetSearchLegalBasisDocuments,
    legalBasisDocuments,
    currentSearchParams,
    page,
    pageCount,
  };
}
